import { z } from 'zod';
import {
  pagingFields,
  parseSearchParams,
  SEARCH_DEFAULT_PAGE_SIZE,
  SEARCH_MAX_PAGE,
  SEARCH_MAX_PAGE_SIZE,
  SEARCH_MAX_TEXT_LENGTH,
  type SearchFieldKinds,
  searchId,
  searchText,
  toSearchParams,
} from './search-spec.ts';

/**
 * Meter search filter spec (Smart Search, MET-3503).
 *
 * One contract shared by the natural-language translator (which emits it), the
 * filterable meters API (which runs it), and the results page (which keeps it
 * in the URL and renders it as editable chips).
 *
 * Fields are flat so each maps 1:1 onto a URL search param. Contact windows
 * are relative day counts, resolved server-side at query time, so a shared
 * link keeps meaning "the last 3 days" instead of going stale.
 *
 * Pagination and sort use the same keys and conventions as
 * `useTableUrlState` (`page` 1-indexed, `pageSize`, `sortBy`, `sortDir`) so
 * the results DataTable can own those params directly.
 */

export const METER_SEARCH_SORT_COLUMNS = [
  'deviceId',
  'address',
  'consumerName',
  'rate',
  'leakRate',
  'backflowRate',
  'lastReadDate',
  'lastContact',
] as const;

/** Metron register product lines (MET-3560). "Astria" is Wave; "VNR" is VN Remote. */
export const METER_SEARCH_PRODUCTS = ['wave', 'vn', 'vnRemote', 'fsk', 'legacy'] as const;

/** The `meter_brand` DB enum, spelled exactly as stored (`derived_meter_brand`). */
export const METER_SEARCH_BRANDS = [
  'Metron',
  'Badger',
  'Sensus',
  'Diehl',
  'Itron',
  'Neptune',
  'Kamstrup',
  'Zenner',
  'Seametrics',
  'Honeywell',
  'Mueller',
  'Unknown',
  'Master Meter',
] as const;

/** Meter source, from `sensor.device_id_schema`. */
export const METER_SEARCH_SOURCES = ['metron', 'waterSignal', 'external'] as const;

/** Meter size classes, the existing Residential / Commercial / Hydrant buckets. */
export const METER_SEARCH_SIZE_CLASSES = ['residential', 'commercial', 'hydrant'] as const;

export const METER_SEARCH_DEFAULT_PAGE_SIZE = SEARCH_DEFAULT_PAGE_SIZE;
export const METER_SEARCH_MAX_PAGE_SIZE = SEARCH_MAX_PAGE_SIZE;
export const METER_SEARCH_MAX_PAGE = SEARCH_MAX_PAGE;
export const METER_SEARCH_MAX_TEXT_LENGTH = SEARCH_MAX_TEXT_LENGTH;
// Ten years of history: a product choice (see search-spec.ts on bounds).
export const METER_SEARCH_MAX_DAYS = 3_650;

const id = searchId;
const days = z.number().int().positive().max(METER_SEARCH_MAX_DAYS);
// Zod 4 numbers already reject Infinity and NaN.
const gpm = z.number().nonnegative();

export const meterSearchSchema = z
  .object({
    customerId: id.optional().describe('Only meters belonging to this customer'),
    propertyId: id.optional().describe('Only meters at this property'),
    sensorGroupId: id.optional().describe('Only meters in this sensor group'),
    text: searchText.optional()
      .describe('Free-text match against device ID, address, or consumer name'),
    lastContactOlderThanDays: days.optional().describe(
      'Meters whose last device communication was more than this many days ago. ' +
        'Meters with no recorded communication also match.',
    ),
    lastContactWithinDays: days.optional()
      .describe('Meters whose last device communication was within this many days'),
    rateMin: gpm.optional().describe('Current flow rate at least this many GPM'),
    rateMax: gpm.optional().describe('Current flow rate at most this many GPM'),
    leakRateMin: gpm.optional().describe('Leak rate at least this many GPM'),
    backflowRateMin: gpm.optional().describe('Backflow rate at least this many GPM'),
    product: z.enum(METER_SEARCH_PRODUCTS).optional().describe(
      'Metron register product line: wave (Wave, also called Astria), vn (all VN registers, ' +
        'including VN Remote), vnRemote (VN Remote / VNR only), fsk (FSK and 2nd-gen FSK), ' +
        'legacy (NRD, M2e, M2g, M2w, R2N, R2-Relay)',
    ),
    meterBrand: z.enum(METER_SEARCH_BRANDS).optional().describe(
      'Brand of the meter body, whatever the register (a VN on a Badger meter is Badger)',
    ),
    source: z.enum(METER_SEARCH_SOURCES).optional().describe(
      'Where the meter comes from: metron (Metron registers), waterSignal, or external ' +
        '(third-party meters sent through DataLink)',
    ),
    sizeClass: z.enum(METER_SEARCH_SIZE_CLASSES).optional()
      .describe('Meter size class: residential (up to 1"), commercial (1.5"–6"), hydrant (8"+)'),
    ...pagingFields(METER_SEARCH_SORT_COLUMNS),
  })
  .strict()
  .refine((s) => s.rateMin === undefined || s.rateMax === undefined || s.rateMin <= s.rateMax, {
    message: 'rateMin must not exceed rateMax',
    path: ['rateMin'],
  })
  .refine(
    (s) =>
      s.lastContactOlderThanDays === undefined || s.lastContactWithinDays === undefined ||
      s.lastContactOlderThanDays < s.lastContactWithinDays,
    {
      message: 'lastContactOlderThanDays must be less than lastContactWithinDays',
      path: ['lastContactOlderThanDays'],
    },
  );

export type MeterSearch = z.infer<typeof meterSearchSchema>;

/** One row of `GET /meters/filter` (MET-3504). Rates are GPM. */
export interface MeterSearchRow {
  sensorId: number;
  deviceId: string;
  address: string | null;
  consumerName: string | null;
  deviceDescription: string | null;
  rate: number | null;
  leakRate: number | null;
  backflowRate: number | null;
  /** ISO timestamp of the meter's last read; null if it never reported a read. */
  lastReadDate: string | null;
  /**
   * ISO timestamp of the meter's last device communication, which is what
   * Smart Search's "contact" filters use; null if none is recorded.
   */
  lastContact: string | null;
  customerId: number | null;
  customerName: string | null;
}

export interface MeterSearchResponse {
  meters: MeterSearchRow[];
  total: number;
  page: number;
  pageSize: number;
}

// URL order matches the previous hand-written codec, so meter URLs are unchanged.
const METER_SEARCH_FIELDS: SearchFieldKinds = {
  customerId: 'number',
  propertyId: 'number',
  sensorGroupId: 'number',
  lastContactOlderThanDays: 'number',
  lastContactWithinDays: 'number',
  rateMin: 'number',
  rateMax: 'number',
  leakRateMin: 'number',
  backflowRateMin: 'number',
  page: 'number',
  pageSize: 'number',
  text: 'string',
  sortBy: 'string',
  sortDir: 'string',
  product: 'string',
  meterBrand: 'string',
  source: 'string',
  sizeClass: 'string',
};

/** Serialize a meter search to URL params (defaults omitted; see `toSearchParams`). */
export function toMeterSearchParams(search: MeterSearch): URLSearchParams {
  return toSearchParams(search, METER_SEARCH_FIELDS);
}

/** Parse URL params into a validated meter search (see `parseSearchParams`). */
export function parseMeterSearchParams(
  params: URLSearchParams,
): ReturnType<typeof meterSearchSchema.safeParse> {
  return parseSearchParams(meterSearchSchema, METER_SEARCH_FIELDS, params);
}
